// Re-export everything from @wander/shared + add 3 web-only realtime functions
import supabase from '@wander/shared/lib/supabase.js';

export * from '@wander/shared/lib/supabase.js';
export default supabase;

// ============================================
// WEB-ONLY: Real-time sync functions
// These use Supabase Realtime + BroadcastChannel API (browser-only)
// Not included in @wander/shared because mobile has its own realtime strategy
// ============================================

/**
 * Subscribe to saved_itineraries changes for a user.
 * Fires callback on INSERT, UPDATE, DELETE from any device/tab.
 * Returns unsubscribe function.
 */
export const subscribeToItineraryChanges = (userId, onChange) => {
  if (!userId) return () => {};

  const channel = supabase
    .channel(`itineraries:${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'saved_itineraries',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        onChange(payload.eventType, payload.new || payload.old);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Cross-tab sync via BroadcastChannel API.
 * When one tab saves/deletes an itinerary, other tabs get notified.
 */
let itineraryBroadcast = null;

export const broadcastItineraryChange = (eventType, data) => {
  try {
    if (!itineraryBroadcast) {
      itineraryBroadcast = new BroadcastChannel('wander_itineraries');
    }
    itineraryBroadcast.postMessage({ eventType, data, timestamp: Date.now() });
  } catch {
    // BroadcastChannel not supported (e.g., Safari < 15.4) — silent fallback
  }
};

/**
 * Subscribe to collaborative trip changes (wishlist, activity log).
 * Returns unsubscribe function.
 */
export const subscribeToTripCollaboration = (tripId, { onWishlistChange, onActivityLog, onPresence } = {}) => {
  if (!tripId) return () => {};

  const channel = supabase
    .channel(`collab:${tripId}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'trip_wishlist',
      filter: `trip_id=eq.${tripId}`,
    }, (payload) => {
      onWishlistChange?.(payload.eventType, payload.new || payload.old);
    })
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'trip_activity_log',
      filter: `trip_id=eq.${tripId}`,
    }, (payload) => {
      onActivityLog?.(payload.new);
    })
    .on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      onPresence?.(state);
    })
    .subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user?.id) {
            await channel.track({ user_id: user.id, online_at: new Date().toISOString() });
          }
        } catch {
          // Ignore auth errors for presence tracking
        }
      }
    });

  return () => {
    supabase.removeChannel(channel);
  };
};

export const onItineraryBroadcast = (callback) => {
  try {
    if (!itineraryBroadcast) {
      itineraryBroadcast = new BroadcastChannel('wander_itineraries');
    }
    itineraryBroadcast.onmessage = (event) => {
      callback(event.data.eventType, event.data.data);
    };
    return () => {
      itineraryBroadcast.onmessage = null;
    };
  } catch {
    return () => {}; // No-op cleanup if unsupported
  }
};
